import {
  Button,
  Checkbox,
  Group,
  Stack,
  Stepper,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { CandidateProfileFields, CandidateReviewSummary } from "./CandidateProfileFields";
import { PhoneInput } from "./PhoneInput";
import { ResumeParseSection } from "./ResumeParseSection";
import { ContentCard } from "./ui";
import { candidatesApi } from "../lib/api";
import { useRoleFamilyCatalog } from "../lib/useRoleFamilyCatalog";
import { ApiError } from "../lib/apiClient";
import {
  educationSummaryFromForm,
  mergeResumeParse,
  optionalEnglishLevel,
  optionalGender,
  RECRUITER_CANDIDATE_FORM_INITIAL,
  type RecruiterCandidateFormValues,
} from "../lib/candidateForm";
import { ctcLakhsLiveError, optionalLakhsToPaise } from "../lib/ctcLakhs";
import { validateOptionalEmail } from "../lib/email";
import { extractDocumentText, validateDocumentFile } from "../lib/extractDocumentText";
import { RECRUITER_WORK_STATUS_OPTIONS } from "../lib/formCatalogs";
import { validateIndianPhoneField, toPhoneForApi, parsePhoneLocal } from "../lib/phone";
import { useSegmentCatalog } from "../lib/useSegmentCatalog";
import { useSkillCatalog } from "../lib/useSkillCatalog";
import { useCityCatalog } from "../lib/useCityCatalog";
import { axioTokens } from "../theme";
import type { Segment } from "../types/api";

const IMPORT_STEP = 0;
const PERSONAL_STEP = 1;
const PROFESSIONAL_STEP = 2;
const REVIEW_STEP = 3;

const DUPLICATE_CANDIDATE_CODE = "duplicate_candidate";
const DUPLICATE_RESUME_CODE = "duplicate_resume";
const DUPLICATE_PHONE_COPY = "Phone already in your pool";
const DUPLICATE_RESUME_COPY = "This resume was already used for another candidate.";
const PERSONAL_FIELDS = ["full_name", "phone", "email", "cities"] as const;
const PROFESSIONAL_FIELDS = ["skills", "segments", "current_ctc_lakhs", "expected_ctc_lakhs"] as const;

interface Props {
  onSuccess?: () => void;
}

function FormSection({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <ContentCard padding="lg">
      <Stack gap="md">
        <div>
          <Title order={5} fw="var(--axio-font-weight-bold)" style={{ letterSpacing: "var(--axio-letter-spacing-tight)" }}>
            {title}
          </Title>
          {subtitle ? (
            <Text size="sm" c="dimmed" mt="var(--axio-unit-4)">
              {subtitle}
            </Text>
          ) : null}
        </div>
        {children}
      </Stack>
    </ContentCard>
  );
}

export function AddCandidateForm({ onSuccess }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeId, setResumeId] = useState<string | null>(null);
  const [resumeBusy, setResumeBusy] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [resumeHint, setResumeHint] = useState<string | null>(null);
  const [active, setActive] = useState(IMPORT_STEP);
  const [parsedFields, setParsedFields] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();
  const { flatOptions: segmentOptions } = useSegmentCatalog();
  const { options: roleFamilyOptions } = useRoleFamilyCatalog();
  const { labels: skillLabels, suggestionLabels, languageSuggestionLabels } = useSkillCatalog();
  const { resolveFromRaw: resolveCity } = useCityCatalog();

  const form = useForm<RecruiterCandidateFormValues>({
    initialValues: { ...RECRUITER_CANDIDATE_FORM_INITIAL },
    validate: {
      full_name: (v) => (v.trim().length >= 2 ? null : "Enter the candidate's name"),
      phone: validateIndianPhoneField,
      email: validateOptionalEmail,
      cities: (v) => (v.length > 0 ? null : "Select at least one city"),
      current_ctc_lakhs: ctcLakhsLiveError,
      expected_ctc_lakhs: ctcLakhsLiveError,
      skills: (v) =>
        v.filter((s) => s.trim()).length >= 5
          ? null
          : "Add at least 5 skills (autofill from resume, then review)",
      segments: (v) => (v.length > 0 ? null : "Select at least one segment"),
      consent_given: (v) => (v ? null : "You must confirm consent to proceed"),
    },
  });

  function fieldsHaveError(keys: readonly string[]) {
    return keys.some((key) => form.validateField(key).hasError);
  }

  function resetLocal() {
    form.reset();
    setResumeFile(null);
    setResumeId(null);
    setResumeError(null);
    setResumeHint(null);
    setActive(IMPORT_STEP);
    setParsedFields(new Set());
  }

  function handleStepClick(step: number) {
    if (step > PERSONAL_STEP && fieldsHaveError(PERSONAL_FIELDS)) {
      setActive(PERSONAL_STEP);
      return;
    }
    if (step > PROFESSIONAL_STEP && fieldsHaveError(PROFESSIONAL_FIELDS)) {
      setActive(PROFESSIONAL_STEP);
      return;
    }
    setActive(step);
  }

  function goNext() {
    if (active === PERSONAL_STEP && fieldsHaveError(PERSONAL_FIELDS)) return;
    if (active === PROFESSIONAL_STEP && fieldsHaveError(PROFESSIONAL_FIELDS)) return;
    setActive((current) => current + 1);
  }

  async function handleResumeChange(file: File | null) {
    setResumeFile(file);
    setResumeId(null);
    setResumeError(null);
    setResumeHint(null);
    if (!file) return;

    const validationError = validateDocumentFile(file, { label: "Resume" });
    if (validationError) {
      setResumeError(validationError);
      setResumeFile(null);
      return;
    }

    setResumeBusy(true);
    try {
      const extractPromise = extractDocumentText(file, { label: "resume" });
      const uploadPromise = candidatesApi.uploadResume(file);

      const [extractResult, uploadResult] = await Promise.allSettled([
        extractPromise,
        uploadPromise,
      ]);

      if (uploadResult.status === "fulfilled") {
        setResumeId(uploadResult.value.resume_id);
      } else {
        const message =
          uploadResult.reason instanceof ApiError
            ? uploadResult.reason.message
            : "Resume upload failed";
        setResumeError(message);
      }

      if (extractResult.status !== "fulfilled") {
        setResumeHint(
          extractResult.reason instanceof Error
            ? extractResult.reason.message
            : "Could not read resume text. Please fill the form manually.",
        );
        return;
      }

      if (uploadResult.status !== "fulfilled") return;

      const parsed = await candidatesApi.parseResume({
        text: extractResult.value,
        resume_id: uploadResult.value.resume_id,
      });

      const { next, parsedKeys } = mergeResumeParse(
        form.values,
        parsed,
        (raw) => resolveCity(raw) ?? raw,
        skillLabels,
      );
      const extra: Partial<RecruiterCandidateFormValues> = { ...next };
      if (parsed.phone && !parsePhoneLocal(form.values.phone)) {
        extra.phone = toPhoneForApi(parsed.phone);
        parsedKeys.add("phone");
      }
      form.setValues({ ...form.values, ...extra });
      setParsedFields(parsedKeys);
      setResumeHint(
        parsed.phone
          ? "Fields filled from the resume, including phone. Review before saving."
          : "Fields filled from the resume. Add phone if missing, then review before saving.",
      );
      setActive(PERSONAL_STEP);
    } catch (err) {
      setResumeError(err instanceof ApiError ? err.message : "Could not parse resume");
    } finally {
      setResumeBusy(false);
    }
  }

  async function handleSubmit(values: RecruiterCandidateFormValues) {
    setSubmitting(true);
    try {
      await candidatesApi.create({
        full_name: values.full_name,
        phone: toPhoneForApi(values.phone),
        email: values.email || undefined,
        cities: values.cities,
        segments: values.segments as Segment[],
        skills: values.skills.map((s) => s.trim()).filter(Boolean),
        languages: values.languages.map((s) => s.trim()).filter(Boolean),
        years_experience: values.work_status === "fresher" ? 0 : values.years_experience,
        current_ctc_paise: optionalLakhsToPaise(values.current_ctc_lakhs),
        expected_ctc_paise: optionalLakhsToPaise(values.expected_ctc_lakhs),
        currency: "INR",
        availability_in_days: values.availability_in_days,
        current_title: values.current_title || undefined,
        education_summary: educationSummaryFromForm(values) || undefined,
        linkedin_url: values.linkedin_url || undefined,
        notes: values.notes || undefined,
        consent_given: values.consent_given,
        resume_id: resumeId ?? undefined,
        gender: optionalGender(values.gender),
        work_status: values.work_status,
        education_level: values.education_level || undefined,
        college_name: values.college_name || undefined,
        degree: values.degree || undefined,
        specialization: values.specialization || undefined,
        graduation_year: values.graduation_year || undefined,
        english_level: optionalEnglishLevel(values.english_level),
        whatsapp_opt_in: values.whatsapp_opt_in,
        role_family_code: values.role_family_code || undefined,
      });
      notifications.show({
        color: "green",
        title: "Candidate added",
        message: `${values.full_name} added to your pool.`,
      });
      queryClient.invalidateQueries({ queryKey: ["candidates"] });
      queryClient.invalidateQueries({ queryKey: ["my-candidates"] });
      resetLocal();
      onSuccess?.();
    } catch (err) {
      if (err instanceof ApiError && err.code === DUPLICATE_CANDIDATE_CODE) {
        form.setFieldError("phone", DUPLICATE_PHONE_COPY);
        setActive(PERSONAL_STEP);
        return;
      }
      if (err instanceof ApiError && err.code === DUPLICATE_RESUME_CODE) {
        setResumeError(DUPLICATE_RESUME_COPY);
        setActive(IMPORT_STEP);
        return;
      }
      const message = err instanceof ApiError ? err.message : "Something went wrong";
      notifications.show({ color: "red", title: "Could not add candidate", message });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={form.onSubmit(handleSubmit, (errors) => {
        if (PERSONAL_FIELDS.some((key) => errors[key])) setActive(PERSONAL_STEP);
        else if (PROFESSIONAL_FIELDS.some((key) => errors[key])) setActive(PROFESSIONAL_STEP);
      })}
    >
      <Stack gap={axioTokens.unit[32]}>
        <Stepper active={active} onStepClick={handleStepClick}>
          <Stepper.Step label="Import" description="Resume or start fresh">
            <FormSection
              title="Resume"
              subtitle="Drop a PDF or DOCX to autofill phone and profile fields — you can edit anything before saving."
            >
              <Stack gap="md">
                <ResumeParseSection
                  variant="flush"
                  file={resumeFile}
                  busy={resumeBusy}
                  error={resumeError}
                  hint={resumeHint}
                  disabled={submitting}
                  title="Parse from resume"
                  description="Phone and profile fields fill automatically when found."
                  onFileChange={handleResumeChange}
                />
                <Button type="button" variant="default" onClick={() => setActive(PERSONAL_STEP)}>
                  Add manually without a resume
                </Button>
              </Stack>
            </FormSection>
          </Stepper.Step>

          <Stepper.Step label="Personal" description="Name, phone, cities">
            <FormSection title="About" subtitle="Core identity and location preferences.">
              <CandidateProfileFields
                form={form}
                parsedFields={parsedFields}
                section="personal"
                showWhatsapp={false}
                aboutLabel="About the candidate"
                namePlaceholder="As on the resume / ID"
                citiesPlaceholder="Where they can work"
                skillSuggestions={suggestionLabels}
                languageSuggestions={languageSuggestionLabels}
                segmentData={segmentOptions}
                segmentLabel="Segments"
                segmentDescription="Which roles fit this candidate?"
                phoneField={
                  <PhoneInput
                    label="Phone"
                    required
                    description=""
                    value={form.values.phone}
                    onChange={(value) => form.setFieldValue("phone", value)}
                    error={form.errors.phone}
                  />
                }
              />
            </FormSection>
          </Stepper.Step>

          <Stepper.Step label="Professional" description="Skills, education, CTC">
            <FormSection title="Professional profile" subtitle="Skills, education, and compensation for matching.">
              <CandidateProfileFields
                form={form}
                parsedFields={parsedFields}
                section="professional"
                showWhatsapp={false}
                ctcRequired={false}
                workStatusData={[...RECRUITER_WORK_STATUS_OPTIONS]}
                skillSuggestions={suggestionLabels}
                languageSuggestions={languageSuggestionLabels}
                segmentData={segmentOptions}
                segmentLabel="Segments"
                segmentDescription="Which roles fit this candidate?"
                roleFamilyData={roleFamilyOptions}
              />
            </FormSection>
          </Stepper.Step>

          <Stepper.Step label="Review" description="Confirm & save">
            <FormSection title="Review & save" subtitle="Notes and consent happen here — this is the only step that saves.">
              <Stack gap="md">
                <CandidateReviewSummary values={form.values} phone={form.values.phone} />
                <Textarea
                  label="Notes"
                  description="Private to your team. Not shown to the candidate."
                  placeholder="Anything the hiring team should know"
                  minRows={2}
                  autosize
                  {...form.getInputProps("notes")}
                />
                <Checkbox
                  label="I confirm this candidate has consented to being represented by me on AxioHire, in line with DPDP requirements."
                  {...form.getInputProps("consent_given", { type: "checkbox" })}
                />
              </Stack>
            </FormSection>
          </Stepper.Step>
        </Stepper>

        <Group justify="space-between">
          <Button type="button" variant="default" disabled={active === IMPORT_STEP} onClick={() => setActive((current) => current - 1)}>
            Back
          </Button>
          {active < REVIEW_STEP ? (
            <Button type="button" color="violet" onClick={goNext} disabled={resumeBusy}>
              Next
            </Button>
          ) : (
            <Button
              type="submit"
              color="violet"
              size="md"
              loading={submitting || resumeBusy}
              data-testid="add-candidate-submit"
            >
              Add to my pool
            </Button>
          )}
        </Group>
      </Stack>
    </form>
  );
}
